/**
 * Workshop version of NewsletterFrame.
 */
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui";
import { cn } from "@/lib/cn";
import { FrameHeader } from "@/workshop/components/shapes/shared/frame-header";

interface NewsletterFrameProps {
  subject?: string;
  preview?: string;
  bodyPreview?: string;
  title?: string;
  agentName?: string;
  onApprove?: () => void;
  onEdit?: () => void;
  onResizeStart?: React.MouseEventHandler;
  className?: string;
  "data-testid"?: string;
}

export function NewsletterFrame({
  subject = "This Week in AI: March 2026",
  preview = "Top stories from the AI ecosystem...",
  bodyPreview = "Newsletter content preview with rich HTML rendering, images, and formatted sections...",
  title = "Weekly Digest #42",
  agentName = "Newsletter",
  onApprove,
  onEdit,
  onResizeStart: _onResizeStart,
  className,
  "data-testid": testId = "newsletter-frame",
}: NewsletterFrameProps) {
  return (
    <div className={cn("denker-glass-frame flex flex-col rounded-frame overflow-hidden", className)} data-testid={testId}>
      <FrameHeader title={title} icon={Icons.Mail} accentColor="bg-frame-newsletter" agentName={agentName} data-testid={`${testId}-header`} />
      <div className="flex flex-1 flex-col gap-1.5 p-2 overflow-hidden">
        <div className="flex flex-col gap-0.5 rounded border border-border p-1.5" data-testid={`${testId}-meta`}>
          <span className="text-canvas-2xs text-muted">Subject</span>
          <span className="text-canvas-sm font-medium text-secondary">{subject}</span>
          <span className="text-canvas-2xs text-muted">{preview}</span>
        </div>
        <div className="flex flex-1 flex-col rounded border border-border bg-white p-1.5 overflow-hidden" data-testid={`${testId}-body`}>
          <p className="whitespace-pre-wrap text-canvas-sm leading-[1.4] text-gray-800 line-clamp-6">{bodyPreview}</p>
        </div>
      </div>
      <div className="flex h-10 w-full items-center gap-1.5 border-t border-glass-stroke-faint px-2">
        <Button
          type="button"
          onClick={onApprove}
          size="sm"
          className="bg-frame-newsletter text-white shadow-none hover:brightness-110"
          data-testid={`${testId}-approve-btn`}
        >
          Approve & Send
        </Button>
        <Button
          type="button"
          onClick={onEdit}
          variant="secondary"
          size="sm"
          data-testid={`${testId}-edit-btn`}
        >
          Edit
        </Button>
      </div>
    </div>
  );
}

export type { NewsletterFrameProps };
